import React from 'react';
import './Card.css';

export interface CardProps {
  /** Card variant */
  variant?: 'default' | 'clickable';
  /** Card padding size */
  padding?: 'none' | 'sm' | 'md' | 'lg';
  /** Optional header section */
  header?: React.ReactNode;
  /** Card content */
  children: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
  /** Click handler (only for clickable variant) */
  onClick?: () => void;
}

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  (
    {
      variant = 'default',
      padding = 'md',
      header,
      children,
      className = '',
      onClick,
      ...props
    },
    ref
  ) => {
    const classNames = [
      'ui-card',
      `ui-card--${variant}`,
      `ui-card--padding-${padding}`,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const Component = variant === 'clickable' && onClick ? 'button' : 'div';

    return (
      <Component
        ref={ref as any}
        className={classNames}
        onClick={variant === 'clickable' ? onClick : undefined}
        {...props}
      >
        {header && <div className="ui-card__header">{header}</div>}
        <div className="ui-card__content">{children}</div>
      </Component>
    );
  }
);

Card.displayName = 'Card';

export interface CardHeaderProps {
  /** Header title */
  title: string;
  /** Optional subtitle */
  subtitle?: string;
  /** Optional actions (buttons, icons) */
  actions?: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
}

export const CardHeader: React.FC<CardHeaderProps> = ({
  title,
  subtitle,
  actions,
  className = '',
}) => {
  const classNames = ['ui-card-header', className].filter(Boolean).join(' ');

  return (
    <div className={classNames}>
      <div className="ui-card-header__text">
        <h3 className="ui-card-header__title">{title}</h3>
        {subtitle && <p className="ui-card-header__subtitle">{subtitle}</p>}
      </div>
      {actions && <div className="ui-card-header__actions">{actions}</div>}
    </div>
  );
};

CardHeader.displayName = 'CardHeader';
